import React from 'react'
import KaTeXRenderer from '../common/KaTeXRenderer'
import TrueFalseQuestion from './TrueFalseQuestion'
import { BankQuestion } from '../../services/bankQuestionService_moi'

interface QuestionRendererProps {
  question: BankQuestion
  questionNumber: number
  selectedAnswer?: string | Record<string, any>
  onAnswerSelect?: (answer: string | Record<string, any>) => void
  showSolution?: boolean
  className?: string
}

const QuestionRenderer: React.FC<QuestionRendererProps> = ({
  question,
  questionNumber,
  selectedAnswer,
  onAnswerSelect,
  showSolution = false,
  className = ''
}) => {
  const handleAnswerSelect = (answer: string | Record<string, any>) => {
    if (onAnswerSelect && !showSolution) {
      onAnswerSelect(answer)
    }
  }

  // Check if this is a true/false question
  const isTrueFalseQuestion = question.type === 'dung_sai' || question.type === 'true_false' ||
    (question.statement_a || question.statement_b || question.statement_c || question.statement_d)
  
  console.log('🎯 QuestionRenderer - Question type detection:', {
    id: question.id,
    type: question.type,
    isTrueFalseQuestion,
    hasStatements: !!(question.statement_a || question.statement_b || question.statement_c || question.statement_d)
  })

  // If it's a true/false question, use TrueFalseQuestion component
  if (isTrueFalseQuestion) {
    return (
      <div className={`bg-white rounded-xl p-6 border border-gray-200 shadow-sm ${className}`}>
        {/* Question Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-semibold">
              {questionNumber}
            </div>
            <div className="text-base text-gray-500">
              <span className="bg-orange-100 px-2 py-1 rounded text-sm font-medium text-orange-700">
                Đúng/Sai - {question.muc_do}
              </span>
              <span className="ml-2 text-gray-400">
                {question.diem} điểm
              </span>
            </div>
          </div>
        </div>

        {/* True/False Question Component */}
        <TrueFalseQuestion
          question={{
            ...question,
            // Bỏ hoàn toàn cau_hoi, chỉ sử dụng question_lead từ database
            question_lead: question.question_lead
          }}
          answers={typeof selectedAnswer === 'object' ? selectedAnswer : {}}
          onAnswerChange={(questionId, answer) => handleAnswerSelect(answer)}
          showResults={showSolution}
        />
      </div>
    )
  }

  const isCorrectAnswer = (optionIndex: number): boolean => {
    if (typeof question.dap_an_dung === 'string') {
      const correctLetter = question.dap_an_dung.toUpperCase()
      const optionLetter = String.fromCharCode(65 + optionIndex) // A, B, C, D
      return correctLetter === optionLetter
    }
    return false
  }

  const getOptionLetter = (index: number): string => {
    return String.fromCharCode(65 + index) // A, B, C, D
  }

  return (
    <div className={`bg-white rounded-xl p-6 border border-gray-200 shadow-sm ${className}`}>
      {/* Question Header */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center space-x-3">
          <div className="text-base text-gray-500">
            <span className="bg-gray-100 px-2 py-1 rounded text-sm font-medium">
              {question.muc_do}
            </span>
            <span className="ml-2 text-gray-400">
              {question.diem} điểm
            </span>
          </div>
        </div>
      </div>

      {/* Question Content - Chỉ hiển thị cho câu hỏi không phải đúng sai */}
      {!isTrueFalseQuestion && (
        <div className="mb-3">
          <KaTeXRenderer
            content={question.question_lead || question.cau_hoi || ''}
            className="text-gray-900 leading-relaxed text-lg"
            moveImagesToEnd
          />
          
          {question.hinh_anh && (
            <div className="mt-3">
              <img 
                src={question.hinh_anh} 
                alt="Hình ảnh câu hỏi"
                className="w-full max-w-full sm:max-w-[360px] md:max-w-[420px] lg:max-w-[480px] h-auto rounded-lg border border-gray-200 mx-auto block"
              />
            </div>
          )}
        </div>
      )}

      {/* Answer Options */}
      {question.lua_chon && question.lua_chon.length > 0 && (
        <div className="space-y-2 mb-3">
          {question.lua_chon.map((option, index) => {
            const optionLetter = getOptionLetter(index)
            const isSelected = selectedAnswer === optionLetter
            const isCorrect = isCorrectAnswer(index)
            
            let optionClass = 'p-3 border-2 rounded-lg cursor-pointer transition-all duration-200 '
            
            if (showSolution) {
              if (isCorrect) {
                optionClass += 'border-green-500 bg-green-50 text-green-800'
              } else if (isSelected && !isCorrect) {
                optionClass += 'border-red-500 bg-red-50 text-red-800'
              } else {
                optionClass += 'border-gray-200 bg-gray-50 text-gray-600'
              }
            } else {
              if (isSelected) {
                optionClass += 'border-blue-500 bg-blue-50 text-blue-800'
              } else {
                optionClass += 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              }
            }

            return (
              <div
                key={index}
                className={optionClass}
                onClick={() => handleAnswerSelect(optionLetter)}
              >
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full border-2 border-current flex items-center justify-center text-sm font-semibold">
                    {optionLetter}
                  </div>
                  <div className="flex-1">
                    <KaTeXRenderer 
                      content={option}
                      className="leading-relaxed text-base"
                    />
                  </div>
                  {showSolution && isCorrect && (
                    <div className="flex-shrink-0 text-green-600">
                      ✓
                    </div>
                  )}
                  {showSolution && isSelected && !isCorrect && (
                    <div className="flex-shrink-0 text-red-600">
                      ✗
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Solution */}
      {showSolution && question.loi_giai && (
        <div className="border-t pt-4">
          <h4 className="text-base font-semibold text-gray-700 mb-2 flex items-center">
            <span className="w-4 h-4 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm mr-2">
              💡
            </span>
            Lời giải
          </h4>
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <KaTeXRenderer
              content={question.loi_giai}
              className="text-gray-800 leading-relaxed"
              moveImagesToEnd
            />
            
            {question.hinh_anh_giai_thich && (
              <div className="mt-3">
                <img 
                  src={question.hinh_anh_giai_thich} 
                  alt="Hình ảnh giải thích"
                  className="w-full max-w-full sm:max-w-[360px] md:max-w-[420px] lg:max-w-[480px] h-auto rounded border border-blue-300 mx-auto block"
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Hints */}
      {question.goi_y && question.goi_y.length > 0 && !showSolution && (
        <div className="border-t pt-4">
          <h4 className="text-base font-semibold text-gray-700 mb-2">Gợi ý</h4>
          <div className="space-y-2">
            {question.goi_y.map((hint, index) => (
              <div key={index} className="bg-yellow-50 p-3 rounded border border-yellow-200">
                <KaTeXRenderer 
                  content={hint}
                  className="text-gray-700 text-base"
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default QuestionRenderer